import { MouseEvent, memo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Nullable } from '@types'

import { ProfileAlert } from '@modules/alerts/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import ContactAttendeesPopover from '@modules/chat/components/ContactAttendeesPopover'
import useHasContactedMap from '@modules/chat/hooks/useHasContactedMap'
import { ChatType } from '@modules/chat/types'
import { useChatContext } from '@modules/chat/useChatContext'
import { CompanyContactsMap } from '@modules/contacts/types'
import { AnyResultProfile, ProfileType } from '@modules/search/types'

import { PREVIEW_FIELDS_MAP_TO_PROFILES } from '../constants'
import { getCompanyId, getProfileTitle } from '../util/ResultProfile.util'
import ResultProfileItem from './ResultProfileItem'
import { ResultCardItemContainer } from './ResultProfileList.styles'

export interface ResultProfileListProps {
  profiles: AnyResultProfile[]
  companyContactsMap?: CompanyContactsMap
  contactableMap?: Record<string, boolean>
  onProfileNavigate?: (id: string | number) => void
  savedSearchId?: number
  profilesAlertsMap?: Record<string, ProfileAlert>
  newProfilesMap?: Record<string, boolean>
  setScrollX?: (page: number) => void
  setScrollY?: (page: number) => void
}

export const useContactHandler = (profiles: AnyResultProfile[]) => {
  const { openChatModal } = useChatContext()
  const [contactAttendeesPopoverData, setContactAttendeesPopoverData] = useState<
    Nullable<{
      anchor?: Nullable<HTMLElement>
      profile?: AnyResultProfile
    }>
  >(null)

  const contactedMap = useHasContactedMap({ rawProfiles: profiles })

  const getContactHandler = (profile: AnyResultProfile) => {
    if (profile['@type'] === ProfileType.delegate) {
      return () => openChatModal({ delegates: [profile.latest], companyId: profile.latest.companyId, chatType: ChatType.DIRECT })
    }

    return (e: Nullable<MouseEvent<HTMLElement>>) => {
      // eslint-disable-next-line
      if (e?.currentTarget) {
        setContactAttendeesPopoverData({ anchor: e?.currentTarget, profile })
        return
      }

      setContactAttendeesPopoverData(null)
    }
  }

  return {
    openChatModal,
    setContactAttendeesPopoverData,
    contactAttendeesPopoverData,
    contactedMap: profiles.length ? contactedMap : null,
    getContactHandler,
  }
}

const ResultProfileList = ({
  profiles,
  savedSearchId,
  companyContactsMap,
  contactableMap,
  onProfileNavigate,
  profilesAlertsMap,
  newProfilesMap,
  setScrollX,
  setScrollY,
}: ResultProfileListProps) => {
  const { user } = useAuthContext()
  const navigate = useNavigate()

  const { contactAttendeesPopoverData, contactedMap, getContactHandler, setContactAttendeesPopoverData, openChatModal } =
    useContactHandler(profiles)

  return (
    // eslint-disable-next-line
    <>
      {profiles?.map((profile: AnyResultProfile) => (
        <ResultCardItemContainer key={profile.id}>
          <ResultProfileItem
            fields={PREVIEW_FIELDS_MAP_TO_PROFILES[profile['@type']]}
            resultProfile={profile}
            profileAlert={profilesAlertsMap?.[profile.id]}
            companyContactCounts={companyContactsMap?.[getCompanyId(profile)]?.counts}
            recentMeeting={companyContactsMap?.[getCompanyId(profile)]?.recentMeeting}
            renderTitle={() => getProfileTitle(profile)}
            onReadMoreClick={() => {
              if (setScrollX) {
                setScrollX(window.scrollX)
              }
              if (setScrollY) {
                setScrollY(window.scrollY)
              }

              setTimeout(() => {
                navigate(`/plus/profile/${profile.id}/${profile['@type']}`)
                onProfileNavigate?.(profile.id)
              }, 100)
            }}
            isContactButtonEnabled={
              (profile.id !== user?.id && profile['@type'] === ProfileType.delegate) || contactableMap?.[getCompanyId(profile)]
            }
            onContactClick={getContactHandler(profile)}
            hasContacted={
              contactedMap?.[profile['@type'] === ProfileType.delegate ? ProfileType.delegate : ProfileType.company]?.[
                profile['@type'] === ProfileType.delegate ? profile.id : getCompanyId(profile)
              ]
            }
            fieldsGrid={{
              lg: [2, 3, 3],
              md: [2, 5],
            }}
            isViewed={profile._isViewed ?? true}
            isNew={newProfilesMap?.[profile.latest.id] ?? false}
            savedSearchId={savedSearchId}
          />
        </ResultCardItemContainer>
      ))}
      {!!contactAttendeesPopoverData?.profile && (
        <ContactAttendeesPopover
          anchor={contactAttendeesPopoverData?.anchor}
          profile={contactAttendeesPopoverData?.profile}
          close={() => setContactAttendeesPopoverData(null)}
          openChatModal={openChatModal}
        />
      )}
    </>
  )
}

export default memo(ResultProfileList)
